// Brand-theme layer — turns the event's admin-configured button colours
// (EMS → Settings → Branding → Button Colors) into the `--brand-*` CSS
// custom properties that `sass/_variables.scss` builds every primary
// token on top of.
//
// Why a separate layer instead of extending `getDesignStyles`?
// `getDesignStyles` is bound with `:style` on `#app`, which is a *child*
// of `<html>`. A `:root { --color-primary: var(--brand-primary) }`
// declaration resolves `var()` against the element it is declared on —
// `<html>` — so values living on `#app` can never feed it. Writing the
// brand inputs onto `document.documentElement` instead lets the whole
// token graph in `_variables.scss` resolve normally, and also reaches
// content that libraries teleport out of `#app` onto `<body>` (modals,
// toasts, the PWA install prompt).
//
// Theme awareness is deliberately left to CSS: this module publishes
// both a light-mode and a dark-mode accent, and `_variables.scss` picks
// between them under `[data-theme='dark']`. Nothing here has to re-run
// when the user flips the theme.

import {
  contrastRatio,
  darken,
  ensureContrast,
  hslToRgb,
  lighten,
  parseHex,
  readableTextOn,
  relativeLuminance,
  rgbToHsl,
  toHex,
  toRgba,
  toRgbChannels
} from '@/helpers/color';

// Reference surfaces the accents are made legible against. Light-mode
// accent text sits mostly on white cards; dark-mode accent text sits on
// the navy card surface (`--t-surface`), which is the lightest ground
// accents routinely appear on and therefore the conservative choice.
const LIGHT_SURFACE = '#ffffff';
const DARK_SURFACE = '#111824';

// WCAG AA for normal-size text. Dark mode aims higher because thin
// light-on-dark type reads worse at the same measured ratio.
const LIGHT_TARGET = 4.5;
const DARK_MIN = 4.5;
const DARK_TARGET = 7;

// ── Brand ramp ───────────────────────────────────────────────────────
// A full 50-900 scale in the event's hue. `tailwind.config.js` maps its
// `teal-*` utilities onto these, which is what makes the networking /
// dashboard suite follow the event's branding: that feature is built
// almost entirely out of `bg-teal-50`, `text-teal-600`,
// `border-teal-500`, `ring-teal-500/20` and friends, and those used to
// compile to fixed hexes.
//
// Each step is a blend of the accent towards white (lighter steps) or
// black (darker steps). The ratios are measured off the shipped default
// ramp so a branded event's scale keeps the same rhythm the design was
// drawn against, and re-deriving them from `#047873` reproduces that
// ramp to within a couple of levels per channel.
//
// The *accent* is the anchor rather than the raw button fill, because
// most of these steps are used as text, icon and border colours on
// white and have to stay legible — a pale brand would otherwise turn
// every label in the section invisible. Filled buttons are not part of
// this scale; they read `--t-btn` / `--t-btn-on` through the `.btn-brand`
// class so they stay exactly the colour the admin picked.
//
// Each stop is expressed relative to the anchor rather than as an
// absolute colour, so step 600 is always the brand itself:
//
//   `l` — how far to travel towards white (positive) or black
//         (negative), as a fraction of the room available in that
//         direction. Relative, so a light brand doesn't get dragged
//         down to the default's lightness.
//   `s` — saturation as a multiple of the anchor's. The shipped ramp
//         deliberately washes its pale steps out (a fully saturated
//         `-50` tint is too loud behind an icon) and holds saturation
//         on the dark steps; these multipliers reproduce that.
//
// Re-deriving the scale from the default `#047873` reproduces the
// shipped hexes to within 2/255 per channel.
export const BRAND_RAMP_STEPS = [
  50, 100, 200, 300, 400, 500, 600, 700, 800, 900
];

const RAMP_STOPS = {
  50: { l: 0.909, s: 0.46 },
  100: { l: 0.777, s: 0.45 },
  200: { l: 0.609, s: 0.46 },
  300: { l: 0.399, s: 0.43 },
  400: { l: 0.194, s: 0.62 },
  500: { l: 0.049, s: 0.92 },
  600: { l: 0, s: 1 },
  700: { l: -0.153, s: 1.01 },
  800: { l: -0.313, s: 1.02 },
  900: { l: -0.486, s: 1.04 }
};

function rampProperty(step) {
  return `--brand-ramp-${step}`;
}

// The 50-900 scale for an anchor colour, as `{ '--brand-ramp-50': 'r g b', … }`.
function buildRamp(anchor) {
  const base = rgbToHsl(anchor);
  const ramp = {};

  BRAND_RAMP_STEPS.forEach((step) => {
    const stop = RAMP_STOPS[step];
    const room = stop.l >= 0 ? 1 - base.l : base.l;
    ramp[rampProperty(step)] = toRgbChannels(
      hslToRgb({
        h: base.h,
        s: Math.min(1, base.s * stop.s),
        l: base.l + stop.l * room
      })
    );
  });

  return ramp;
}

// Every property this module owns. Listed explicitly so a switch to an
// event with no branding clears exactly what a branded event set,
// letting the static defaults in `_variables.scss` take over again.
export const BRAND_PROPERTIES = [
  '--brand-primary',
  '--brand-primary-hover',
  '--brand-on-primary',
  '--brand-accent',
  '--brand-accent-hover',
  '--brand-soft',
  '--brand-accent-dark',
  '--brand-accent-dark-hover',
  '--brand-soft-dark',
  '--brand-on-accent-dark',
  '--brand-secondary',
  '--brand-secondary-hover',
  '--brand-on-secondary',
  ...BRAND_RAMP_STEPS.map(rampProperty)
];

// A fill this dark can't be darkened any further to signal hover, so the
// hover state lifts instead.
const DARK_FILL_LUMINANCE = 0.06;

function hoverFor(rgb) {
  return relativeLuminance(rgb) <= DARK_FILL_LUMINANCE
    ? lighten(rgb, 0.16)
    : darken(rgb, 0.12);
}

// Honours an explicitly configured text colour. The admin picked it on
// purpose, so it is only replaced when it is missing, unparseable, or so
// close to the fill that the label would be invisible (below the 3:1
// floor WCAG allows for large/bold text, which is what buttons use).
function textOn(fill, configured) {
  const chosen = parseHex(configured);
  if (chosen && contrastRatio(chosen, fill) >= 3) return toHex(chosen);
  return readableTextOn(fill);
}

// Builds the `--brand-*` map for a branding payload, or null when the
// payload carries no usable primary colour.
//
// `branding` is the object the live-event API returns under
// `eventDetails.branding` — `livePrimaryButtonColor`,
// `livePrimaryButtonTextColor`, `liveSecondaryButtonColor`,
// `liveSecondaryButtonTextColor`.
export function buildBrandTokens(branding) {
  if (!branding) return null;

  const primary = parseHex(branding.livePrimaryButtonColor);
  if (!primary) return null;

  // Light-mode accent: darkened only if the fill can't carry text on
  // white. A brand that already passes is used verbatim.
  const accent = ensureContrast(primary, LIGHT_SURFACE, {
    minRatio: LIGHT_TARGET,
    preferredRatio: LIGHT_TARGET
  });

  // Dark-mode accent: lifted in HSL so the hue survives.
  const accentDark = ensureContrast(primary, DARK_SURFACE, {
    minRatio: DARK_MIN,
    preferredRatio: DARK_TARGET
  });

  const tokens = {
    // Buttons use the same contrast-safe shade as every other brand
    // element, so fills, badges and icons always match. A brand that
    // already passes is unchanged (accent === primary).
    '--brand-primary': toHex(accent),
    '--brand-primary-hover': toHex(hoverFor(accent)),
    '--brand-on-primary': textOn(accent, branding.livePrimaryButtonTextColor),

    '--brand-accent': toHex(accent),
    '--brand-accent-hover': toHex(hoverFor(accent)),
    '--brand-soft': toRgba(accent, 0.12),

    '--brand-accent-dark': toHex(accentDark),
    '--brand-accent-dark-hover': toHex(lighten(accentDark, 0.15)),
    '--brand-soft-dark': toRgba(accentDark, 0.15),
    // Label colour for text sitting *on* the lifted accent. The accent
    // is bright by construction, so this normally resolves to the dark
    // end; a neutral is used rather than a teal-tinted black so it also
    // reads correctly under a red or amber brand.
    '--brand-on-accent-dark': readableTextOn(accentDark)
  };

  // The ramp Tailwind's `teal-*` utilities resolve against. Published as
  // raw `r g b` channels so `ring-teal-500/20` and the other opacity
  // modifiers the networking pages use keep working.
  Object.assign(tokens, buildRamp(accent));

  // Secondary is optional — plenty of events only ever configure the
  // primary button. When it is absent the static default stands.
  const secondary = parseHex(branding.liveSecondaryButtonColor);
  if (secondary) {
    tokens['--brand-secondary'] = toHex(secondary);
    tokens['--brand-secondary-hover'] = toHex(hoverFor(secondary));
    tokens['--brand-on-secondary'] = textOn(
      secondary,
      branding.liveSecondaryButtonTextColor
    );
  }

  return tokens;
}

// Writes the map onto `<html>`, clearing any property the new map omits
// so switching events never leaves a stale colour behind. Passing null
// removes the whole layer.
export function applyBrandTokens(tokens) {
  if (typeof document === 'undefined' || !document.documentElement) return;

  const style = document.documentElement.style;

  BRAND_PROPERTIES.forEach((property) => {
    const value = tokens && tokens[property];
    if (value) style.setProperty(property, value);
    else style.removeProperty(property);
  });
}

// Convenience wrapper for the store subscription.
export function applyBrandingToDocument(branding) {
  applyBrandTokens(buildBrandTokens(branding));
}
